// source: https://github.com/sindresorhus/array-move/blob/main/index.d.ts

import type { ElementOf } from 'ts-essentials'

export function arrayMoveMutable(array: unknown[], fromIndex: number, toIndex: number) {
  const startIndex = fromIndex < 0 ? array.length + fromIndex : fromIndex

  if (startIndex >= 0 && startIndex < array.length) {
    const endIndex = toIndex < 0 ? array.length + toIndex : toIndex

    const [item] = array.splice(fromIndex, 1)
    array.splice(endIndex, 0, item)
  }
}

export function arrayMoveImmutable<ValueType extends unknown[]>(array: ValueType, fromIndex: number, toIndex: number) {
  const newArray = structuredClone(array)
  arrayMoveMutable(newArray, fromIndex, toIndex)
  return newArray
}

export const isLast = (array: unknown[], index: number) => {
  return index === array.length - 1
}

/**
 * Update an array without mutating the orignal one
 * @param array The array you want to update
 * @param index The position of the element
 * @param value The new value to insert
 */
export const replaceAtImmutable = <T extends unknown[]>(array: T, index: number, value: ElementOf<T>): T => {
  return [
    ...array.slice(0, index),
    value,
    ...array.slice(index + 1),
  ] as T
}

/**
 * Update an array without mutating the orignal one using native array methods
 * @param array The array you want to update
 * @param index The position of the element
 * @param value The new value to insert
 */
export const replaceAtImmutable2 = <T extends unknown[]>(array: T, index: number, value: ElementOf<T>): T => {
  // TODO: [2023-09-01] remove eslint disabled rule once in the language
  // @ts-expect-error
  return array.with(index, value)
}

/**
 * Remove an element at the specified index
 * @param array The array you want to update
 * @param index The position of the element
 */
export const immutableRemoveAt = <T extends unknown[]>(array: T, index: number): T => {
  return [
    ...array.slice(0, index),
    ...array.slice(index + 1),
  ] as T
}


export const arrayInArray = (arr1: readonly any[], arr2: readonly any[]) => {
  return arr1.some(ai => arr2.includes(ai))
}

export const removeDuplicates = <U, T extends readonly U[]>(key: keyof U) => (obj: U, index: number, self: U[]) => {
  return index === self.findIndex((t) => (
    t[key] === obj[key]
  ))
}


export const arrayInsertAtImmutable = <T>(array: readonly T[], index: number, element: T): T[] => {
  const length = array.length
  if (index < -length || index > length) {
    throw new Error("Index out of range")
  }

  const positiveIndex = index < 0 ? length + index : index

  return [...array.slice(0, positiveIndex), element, ...array.slice(positiveIndex)]
}

export const isSame = <T>(array1: T[], array2: T[]) => (array1.length == array2.length) && array1.every(el => array2.includes(el))

/**
 * @param array - the base array
 * @param callback - should return a group name
 */
export const groupBy = <T, U extends string>(array: T[], predicate: (value: T, index: number, array: T[]) => U) => {
  // eslint-disable-next-line unicorn/no-array-reduce
  return array.reduce((acc, value, index, array) => {
    (acc[predicate(value, index, array)] ||= []).push(value)
    return acc
  }, {} as { [key in ReturnType<typeof predicate>]: T[] })
}
